import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
	IconArrowLeft,
	IconBan,
	IconMail,
	IconShieldCheck,
} from "@tabler/icons-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/convex-api";

const STATE_LABELS: Record<string, string> = {
	posted: "Posted",
	negotiation: "Negotiation",
	awaiting_agreement_confirmation: "Awaiting Agreement",
	funds_staged: "Funds Staged",
	in_progress: "In Progress",
	awaiting_delivery_confirmation: "Awaiting Delivery",
	completed: "Completed",
	disputed: "Disputed",
	cancelled: "Cancelled",
};

export const Route = createFileRoute("/_authenticated/customers/$userId")({
	component: CustomerDetailPage,
});

function formatNaira(kobo: number): string {
	return `₦${(kobo / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function CustomerDetailPage() {
	const { userId } = Route.useParams();
	const data = useQuery(api.admin.getUserDetails, {
		userId: userId as never,
	});
	const banUser = useMutation(api.admin.banUser);
	const unbanUser = useMutation(api.admin.unbanUser);

	const [banReason, setBanReason] = useState("");
	const [showBanForm, setShowBanForm] = useState(false);
	const [isBanning, setIsBanning] = useState(false);
	const [isUnbanning, setIsUnbanning] = useState(false);
	const [message, setMessage] = useState<string | null>(null);

	if (data === undefined) {
		return (
			<div className="container mx-auto space-y-6 px-4 py-10 lg:px-6">
				<Skeleton className="h-8 w-48" />
				<div className="grid gap-6 lg:grid-cols-2">
					<Skeleton className="h-64" />
					<Skeleton className="h-64" />
				</div>
			</div>
		);
	}

	if (data === null) {
		return (
			<div className="container mx-auto px-4 py-10 lg:px-6">
				<p className="text-muted-foreground">Customer not found.</p>
				<Button asChild className="mt-4" variant="outline">
					<Link to="/customers">Back to Customers</Link>
				</Button>
			</div>
		);
	}

	const { user, wallet, recentErrands, recentTransactions } = data;

	const handleBan = async () => {
		if (!banReason.trim()) {
			setMessage("Please provide a ban reason.");
			return;
		}
		setIsBanning(true);
		setMessage(null);
		try {
			await banUser({
				userId: userId as never,
				reason: banReason.trim(),
			});
			setMessage("User banned.");
			setShowBanForm(false);
			setBanReason("");
		} catch {
			setMessage("Failed to ban user.");
		} finally {
			setIsBanning(false);
		}
	};

	const handleUnban = async () => {
		setIsUnbanning(true);
		setMessage(null);
		try {
			await unbanUser({ userId: userId as never });
			setMessage("User unbanned.");
		} catch {
			setMessage("Failed to unban user.");
		} finally {
			setIsUnbanning(false);
		}
	};

	return (
		<div className="container mx-auto px-4 py-10 lg:px-6">
			<div className="mb-6 flex items-center gap-4">
				<Button asChild size="sm" variant="ghost">
					<Link to="/customers">
						<IconArrowLeft className="mr-1 h-4 w-4" />
						Back
					</Link>
				</Button>
				<h1 className="font-bold text-2xl">{user.name}</h1>
				<div className="flex gap-1">
					{user.roles.map((role: string) => (
						<Badge key={role} variant="secondary">
							{role}
						</Badge>
					))}
				</div>
			</div>

			<div className="grid gap-6 lg:grid-cols-2">
				{/* User Info */}
				<Card>
					<CardHeader>
						<CardTitle>Personal Information</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<div className="flex items-center gap-2 text-sm">
							<IconMail className="h-4 w-4 text-muted-foreground" />
							<span>{user.email}</span>
						</div>
						{user.birthday && (
							<div className="text-sm">
								<span className="text-muted-foreground">Birthday:</span>{" "}
								{user.birthday}
							</div>
						)}
						<div className="text-sm">
							<span className="text-muted-foreground">Joined:</span>{" "}
							{new Date(user.createdAt).toLocaleDateString()}
						</div>
						{user.referralCode && (
							<div className="text-sm">
								<span className="text-muted-foreground">Referral Code:</span>{" "}
								{user.referralCode}
							</div>
						)}
						{user.pointsBalance != null && (
							<div className="text-sm">
								<span className="text-muted-foreground">Points:</span>{" "}
								{user.pointsBalance}
								{user.tier && (
									<Badge className="ml-2" variant="outline">
										{user.tier}
									</Badge>
								)}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Wallet */}
				<Card>
					<CardHeader>
						<CardTitle>Wallet</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						{wallet ? (
							<>
								<div className="text-sm">
									<span className="text-muted-foreground">Available:</span>{" "}
									<span className="font-semibold">
										{formatNaira(wallet.availableBalance)}
									</span>
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Locked:</span>{" "}
									{formatNaira(wallet.lockedBalance)}
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">Status:</span>{" "}
									<Badge variant="secondary">{wallet.status}</Badge>
								</div>
								<div className="text-sm">
									<span className="text-muted-foreground">DVA:</span>{" "}
									{wallet.paystackBankName} — {wallet.paystackAccountNumber}
								</div>
								<div className="text-muted-foreground text-xs">
									{wallet.paystackAccountName}
								</div>
							</>
						) : (
							<p className="text-muted-foreground text-sm">No wallet found.</p>
						)}
					</CardContent>
				</Card>
			</div>

			{/* Tabs: Errands & Transactions */}
			<div className="mt-6">
				<Tabs defaultValue="errands">
					<TabsList>
						<TabsTrigger value="errands">
							Recent Errands ({recentErrands.length})
						</TabsTrigger>
						<TabsTrigger value="transactions">
							Transactions ({recentTransactions.length})
						</TabsTrigger>
					</TabsList>
					<TabsContent value="errands" className="mt-4">
						{recentErrands.length === 0 ? (
							<p className="text-muted-foreground text-sm">No errands.</p>
						) : (
							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Title</TableHead>
											<TableHead>State</TableHead>
											<TableHead>Created</TableHead>
											<TableHead>Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{recentErrands.map(
											(errand: (typeof recentErrands)[number]) => (
												<TableRow key={errand._id}>
													<TableCell className="max-w-[200px] truncate font-medium">
														{errand.title}
													</TableCell>
													<TableCell>
														<Badge variant="secondary">
															{STATE_LABELS[errand.state] ?? errand.state}
														</Badge>
													</TableCell>
													<TableCell>
														{new Date(errand.createdAt).toLocaleDateString()}
													</TableCell>
													<TableCell>
														<Button asChild size="sm" variant="ghost">
															<Link
																to="/errands/$errandId"
																params={{ errandId: errand._id }}
															>
																View
															</Link>
														</Button>
													</TableCell>
												</TableRow>
											),
										)}
									</TableBody>
								</Table>
							</div>
						)}
					</TabsContent>
					<TabsContent value="transactions" className="mt-4">
						{recentTransactions.length === 0 ? (
							<p className="text-muted-foreground text-sm">No transactions.</p>
						) : (
							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Type</TableHead>
											<TableHead>Amount</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Created</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{recentTransactions.map(
											(tx: (typeof recentTransactions)[number]) => (
												<TableRow key={tx._id}>
													<TableCell>
														<Badge variant="outline">{tx.type}</Badge>
													</TableCell>
													<TableCell>{formatNaira(tx.amount)}</TableCell>
													<TableCell>
														<Badge
															variant={
																tx.status === "completed"
																	? "default"
																	: "secondary"
															}
														>
															{tx.status}
														</Badge>
													</TableCell>
													<TableCell>
														{new Date(tx.createdAt).toLocaleString()}
													</TableCell>
												</TableRow>
											),
										)}
									</TableBody>
								</Table>
							</div>
						)}
					</TabsContent>
				</Tabs>
			</div>

			{/* Ban / Unban */}
			<Card className="mt-6 border-red-200">
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-red-600">
						Account Actions
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="flex gap-3">
						{!showBanForm ? (
							<>
								<Button
									onClick={() => setShowBanForm(true)}
									variant="destructive"
								>
									<IconBan className="mr-2 h-4 w-4" />
									Ban User
								</Button>
								<Button
									disabled={isUnbanning}
									onClick={handleUnban}
									variant="outline"
								>
									<IconShieldCheck className="mr-2 h-4 w-4" />
									{isUnbanning ? "Unbanning..." : "Unban User"}
								</Button>
							</>
						) : (
							<div className="w-full space-y-3">
								<Textarea
									onChange={(e) => setBanReason(e.target.value)}
									placeholder="Reason for ban..."
									value={banReason}
								/>
								<div className="flex gap-3">
									<Button
										disabled={isBanning}
										onClick={handleBan}
										variant="destructive"
									>
										{isBanning ? "Banning..." : "Confirm Ban"}
									</Button>
									<Button
										onClick={() => {
											setShowBanForm(false);
											setBanReason("");
										}}
										variant="outline"
									>
										Cancel
									</Button>
								</div>
							</div>
						)}
					</div>
					{message && (
						<p className="text-muted-foreground text-sm">{message}</p>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
